import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ProjectNames } from '~src/projects/resources/ProjectNames';
import { IProjectName } from '~src/projects/ServerApi/types/IProject';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { ITag } from '~src/tags/ITag';
import { Tags } from '~src/tags/resources/Tags';
import { UserNames } from '~src/userManagement/resources/UserNames';

import { DataConnectionTypes } from '../../datasource/dataConnection/resources/DataConnectionTypes';
import { IDataConnectionType } from '../../datasource/dataConnection/serverApi/types/IDataConnection';
import { ISliceable } from '../../httpClient/fetchers/ISliceable';
import { ITypeaheadSource } from '../../httpClient/fetchers/ITypeaheadSource';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { IFieldValue } from '../../httpClient/serverApi/types/IFieldValue';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { SchedulesService } from '../../schedule/resources/Schedules';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { describeLegacyAdminSchedule } from '../../schedule/utils/ScheduleFrequencyDescriptionFilter';
import {
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { ISiteNameSansId } from '../../siteManagement/serverApi/types/ISiteNameSansId';
import { SiteNameSansIds } from '../../siteManagement/services/resources/SiteNameSansIds';
import { ScheduleContentType } from '../../spacesPlatform/models/ContentTypes';
import { FilterFieldNames } from '../../spacesPlatform/models/IFilterClause';
import { NamedItemKeys } from '../../spacesPlatform/models/resourceParts';
import { Groups } from '../../userManagement/groups/Groups';
import { GroupKeys, IGroup } from '../../userManagement/groups/serverApi/types/IGroup';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../userManagement/services/resources/SiteUsers';
import { Users } from '../../userManagement/services/resources/Users';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { IUserName, UserKeys } from '../../userManagement/users/serverApi/types/IUser';

export namespace SearchSuggestionsService {

  export function project(id: string): Promise<IFieldValue> {
    if (id === 'PersonalSpace') {
      return Promise.resolve({
        value: 'PersonalSpace',
        label: Messages.personalSpaceTitle(),
        count: 0
      });
    }
    return ProjectNames.getById(id).then((projectName: IProjectName) => {
      return {
        value: projectName ? projectName.id : id,
        label: projectName ? projectName.name : '',
        count: 0
      };
    });
  }

  export function sites(matchText: string): ISliceable<ISiteNameSansId> {
    return SiteNameSansIds.instance.all().withFilters([Filter.search(matchText)]);
  }

  export function users(matchText: string): ResourceQuery<IUserName> {
    return UserNames.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(UserKeys.displayName)]);
  }

  export function usersWithoutGuest(matchText: string): ResourceQuery<IUserName> {
    return users(matchText).addFilter(Users.instance.getWithoutGuestFilter());
  }

  export function addWithoutGuestFilter(resourceQuery: ResourceQuery<IUserName>): ResourceQuery<IUserName> {
    return resourceQuery.addFilter(Users.instance.getWithoutGuestFilter());
  }

  export function addProjectOwnerFilter(resourceQuery: ResourceQuery<IUserName>): ResourceQuery<IUserName> {
    return resourceQuery.addFilter(Users.instance.getCanOwnProjectFilter());
  }

  export function addMetricOwnerFilter(resourceQuery: ResourceQuery<IUserName>): ResourceQuery<IUserName> {
    return resourceQuery.addFilter(Users.instance.getCanOwnMetricFilter());
  }

  export function addDatasourceOwnerFilter(resourceQuery: ResourceQuery<IUserName>): ResourceQuery<IUserName> {
    return resourceQuery.addFilter(Users.instance.getCanOwnDatasourceFilter());
  }

  export function currentLoggedInUser(matchText: string): ResourceQuery<IUserName> {
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    return users(matchText).addFilter(Users.instance.getByUserIdsFilter([userId]));
  }

  export function user(id: string): Promise<IFieldValue> {
    return UserNames.getById(id).then((userName: IUserName) => {
      return {
        value: userName ? userName.id : id,
        label: userName ? userName.displayName : '',
        count: 0
      };
    });
  }

  export function groups(matchText: string): ISliceable<IGroup> {
    return Groups.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(GroupKeys.name)])
      .withExtraParams({includeUserCount: false});
  }

  export function tags(matchText: string): ISliceable<ITag> {
    return Tags.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(NamedItemKeys.name)]);
  }

  export function dataConnectionType(dataConnectionType: string): Promise<IFieldValue> {
    return DataConnectionTypes.getByDataConnectionType(dataConnectionType).then((connectionType: IDataConnectionType) => {
      return {
        value: connectionType ? connectionType.connectionType : dataConnectionType,
        label: connectionType ? connectionType.connectionTypeDisplayName : '',
        count: 0
      };
    });
  }

  export function schedulesSource(scheduledActionType?: string): ITypeaheadSource {
    const searchFilterFn = (schedule: IScheduleInfo, search: string) => {
      if (!search) return true;
      const scheduleName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName() ? describeLegacyAdminSchedule(schedule) : schedule.name;
      return _.all(search.toLowerCase().split(' '), (searchPart) => (scheduleName.toLowerCase().indexOf(searchPart) >= 0));
    };

    let scheduleResourceQuery = SchedulesService.all().withOrder(ScheduleContentType.defaultOrder);
    if (scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(SchedulesService.filterByActionType(scheduledActionType));
    }

    const scheduleListPromise = SliceableHelper.fetchAll(scheduleResourceQuery).then((schedulesList) => schedulesList.items);

    return SliceableHelper.searchArray(searchFilterFn, scheduleListPromise);
  }

  export function schedulesQuery(scheduledActionType?: string): ResourceQuery<IScheduleInfo> {
    let scheduleResourceQuery = SchedulesService.all().withOrder(ScheduleContentType.defaultOrder);
    if (scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(SchedulesService.filterByActionType(scheduledActionType));
    }
    return scheduleResourceQuery;
  }

  // gets users with siteRole filter and search string
  export function usersBySiteRoleAndSearch(siteRole: string, searchText: string, resultOrder: IOrderClause<string>): ResourceQuery<ISiteUserInfo> {
    if (siteRole === SiteRolesResource.AnyRole) {
      return SiteUsers.instance.all().withOrder([resultOrder])
        .addFilter(Filter.search(searchText));
    }
    return SiteUsers.instance.all().withOrder([resultOrder])
      .addFilter(Filter.stringFieldEq(FilterFieldNames.siteRole, siteRole))
      .addFilter(Filter.search(searchText));
  }
}

export type SearchSuggestionsService = typeof SearchSuggestionsService;
